import React from 'react';
import { duckify, filterDuck } from 'utils/duckify';
import { billingDashboard as billingDashboardDuck, historicalOverdueReport as historicalOverdueDuck } from 'ducks';
import moment from 'moment';
import './styles/BillingDashboard.scss';
import queryString from 'query-string';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import MonthYearFilter from 'components/shared/MonthYearFilter';
import RadioFilter from 'components/shared/RadioFilter';
import FlashRoot from 'components/FlashMessage/FlashRoot';
import PropTypes from 'prop-types';
import Placeholder from '../Placeholder';
import Routes from '../../../routes';
import {
  LineGraph, PieChart, BarGraph, formatTooltipCurrency, formatYLabelsCurrency, formatYLabelsPercent
} from '../graphing/Graphs';
import { formatCurrency } from '../../utils';
import DuckLoader from '../DuckLoader';

const defaultStartDate = moment().subtract(5, 'months').startOf('month');
const defaultEndDate = moment().endOf('month');

const BillingDashboard = ({
  billingDashboard, historicalOverdueReport, location
}) => (
  <div>
    <h2>Billing Dashboard</h2>

    <FlashRoot />

    <div className='billing-dashboard'>
      <HistoricalOverdue historicalOverdueReport={historicalOverdueReport} location={location} />
      <hr />
      <DateFilteredCharts billingDashboard={billingDashboard} location={location} />
    </div>
  </div>
);
BillingDashboard.propTypes = {
  billingDashboard: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    totals: PropTypes.object,
    charged_collected: PropTypes.object,
    payment_success: PropTypes.object,
    payment_type: PropTypes.object,
  })).isRequired,
  historicalOverdueReport: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    datasets: PropTypes.array
  })).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string
  }).isRequired
};

const HistoricalOverdue = ({
  historicalOverdueReport, location
}) => {
  const labels = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  const overdueFilterOptions = { overdue_30: '0-30 Days', overdue_60: '31-60 Days', overdue_90: '61-90 Days', overdue_91_plus: '91+ Days', overdue_total: 'Total' };
  const ready = historicalOverdueReport && historicalOverdueReport.ready && historicalOverdueReport[0];
  const datasets = ready ? historicalOverdueReport[0].datasets : [];

  const jumpToAgingReport = (element) => {
    if (!element.length) return;
    const { datasetIndex, index } = element[0];
    window.open(Routes.aging_admin_billing_path({
      date: moment([datasets[datasetIndex].label, index, 1]).endOf('month').format('YYYY-MM-DD')
    }));
  };

  return (
    <div className='billing-dashboard-historical-section'>
      <div className='billing-dashboard-historical-filter'>
        <RadioFilter
          location={location}
          label='Overdue By:'
          fieldName='overdue_filter'
          defaultOption='overdue_30'
          model='overdue_filter'
          options={overdueFilterOptions}
          hideSubmit={true}
        />
      </div>
      <div className='billing-dashboard-historical-graph'>
        <Placeholder.loading ready={ready}>
          <LineGraph
            xLabels={labels}
            datasets={datasets}
            onClick={jumpToAgingReport}
            title='Historical Percentage of Overdue'
            formatYLabels={formatYLabelsPercent}
            buildTooltip={(context) => formatTooltipCurrency(
              context.dataset.label,
              datasets[context.datasetIndex].points[context.dataIndex].tooltip_value
            )}
            className='billing-dashboard-graph'
          />
        </Placeholder.loading>
      </div>
    </div>
  );
};
HistoricalOverdue.propTypes = {
  historicalOverdueReport: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    datasets: PropTypes.array
  })).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string
  }).isRequired
};

const DateFilteredCharts = ({
  billingDashboard, location
}) => {
  const ready = billingDashboard && billingDashboard.ready && billingDashboard[0];
  const dashboard = ready ? billingDashboard[0] : {};

  return (
    <div className='billing-dashboard-date-section'>
      <div className='billing-dashboard-date-bar'>
        <MonthYearFilter
          location={location}
          defaultStartDate={defaultStartDate.toDate()}
          defaultMonthRange={5}
        />
        { ready && <TotalsBar totals={dashboard.totals} /> }
      </div>
      <DuckLoader
        ducks={[billingDashboard]}
        content={() => (
          <React.Fragment>
            <div className='billing-dashboard-charged-collected'>
              <ChargedVCollected report={dashboard.charged_collected} />
            </div>
            <div className='billing-dashboard-pies'>
              <div className='billing-dashboard-pie'>
                <PaymentSuccessRate report={dashboard.payment_success} />
              </div>
              <div className='billing-dashboard-pie'>
                <PaymentType report={dashboard.payment_type} />
              </div>
            </div>
          </React.Fragment>
        )}
      />
    </div>
  );
};
DateFilteredCharts.propTypes = {
  billingDashboard: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    totals: PropTypes.object,
    charged_collected: PropTypes.object,
    payment_success: PropTypes.object,
    payment_type: PropTypes.object,
  })).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string
  }).isRequired
};

const TotalsBar = ({
  totals
}) => (
  <table className='billing-dashboard-totals'>
    <thead>
      <tr>
        <th>Total Charged</th>
        <th>Total Paid</th>
        <th>Total Remaining</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>{formatCurrency(totals.charged).split('.')[0]}</td>
        <td>{formatCurrency(totals.paid).split('.')[0]}</td>
        <td>{formatCurrency(totals.remaining).split('.')[0]}</td>
      </tr>
    </tbody>
  </table>
);
TotalsBar.propTypes = {
  totals: PropTypes.shape({
    charged: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    remaining: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    overdue: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired
};
const ChargedVCollected = ({
  report
}) => (
  <BarGraph
    xLabels={report.columns}
    datasets={report.datasets}
    title='Charged vs. Collected'
    buildTooltip={(context) => formatTooltipCurrency(context.dataset.label, context.parsed.y)}
    tooltipSort={(a, b) => b.datasetIndex - a.datasetIndex}
    formatYLabels={formatYLabelsCurrency}
    className='billing-dashboard-graph'
  />
);
ChargedVCollected.propTypes = {
  report: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.string),
    datasets: PropTypes.array
  }).isRequired
};

const pieCurrencyTooltip = (context) => formatTooltipCurrency(context.dataset.label, context.parsed).split('.')[0];

const PaymentSuccessRate = ({
  report
}) => (
  <PieChart
    title='Payment Success'
    className='billing-dashboard-pie-graph'
    buildTooltip={pieCurrencyTooltip}
    data={report.data}
  />
);
PaymentSuccessRate.propTypes = {
  report: PropTypes.shape({
    data: PropTypes.array
  }).isRequired
};

const PaymentType = ({
  report
}) => (
  <PieChart
    title='Payment Type'
    className='billing-dashboard-pie-graph'
    buildTooltip={pieCurrencyTooltip}
    data={report.data}
  />
);
PaymentType.propTypes = {
  report: PropTypes.shape({
    data: PropTypes.array
  }).isRequired
};

const mapDucksToProps = () => {
  const parsedSearch = location.search ? queryString.parse(location.search) : {};
  // default to past six months
  const dateRange = {
    start_date: parsedSearch.start_date || defaultStartDate.format('YYYY-MM-DD'),
    end_date: parsedSearch.end_date || defaultEndDate.format('YYYY-MM-DD')
  };
  const historicalOverdueFilter = {
    overdue_filter: parsedSearch.overdue_filter || 'overdue_30'
  };
  return {
    billingDashboard: filterDuck(billingDashboardDuck, dateRange),
    historicalOverdueReport: filterDuck(historicalOverdueDuck, historicalOverdueFilter)
  };
};

export default compose(
  withRouter,
  duckify(mapDucksToProps)
)(BillingDashboard);
